import dayjs from 'dayjs';

// Seat map SVG contract (see docs/SEAT_MAP_SVG_CONTRACT.md):
// - every numbered seat shape has id="<row><number>", for example id="A4";
// - its seat number <text> has id="<row><number>-num";
// - seats drawn grey in the file are permanently out of sale and blocked in every session.
// The snapshot only greys out seats; it never recolours a seat, so drawn-grey seats stay grey.
export const SEAT_MAP_UNAVAILABLE_FILL = '#CBCBCB';
export const SEAT_MAP_UNAVAILABLE_TEXT = '#777777';
export const SEAT_MAP_PNG_SCALE = 2;

const SEAT_MAP_TIME_ZONE = 'Asia/Jakarta';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const FONT_FAMILY = "'Montserrat','Poppins','Helvetica Neue',Arial,sans-serif";
const TEXT_DARK = '#13204B';
const TEXT_MUTED = '#555555';
const PRESTO_FILL = '#EAC897';
const ALLEGRO_FILL = '#1C82C4';
const UNASSIGNED_FILL = '#F5B544';
const UNASSIGNED_INK = '#714500';
const UNASSIGNED_BACKGROUND = '#FFF4D6';
const RESERVATION_PANEL_WIDTH = 276;
const RESERVATION_PANEL_GAP = 20;
const RESERVATION_SYMBOL_COLUMNS = 10;
const MISSING_SEATS_SHOWN = 20;
// Layout units are relative to a 700-unit-wide map so text scales with each venue's viewBox.
const BASE_MAP_WIDTH = 700;
const HEADER_HEIGHT = 112;
const LEGEND_HEIGHT = 52;
// Designer exports can place outer seats on the viewBox edge.
const SIDE_PADDING = 24;

const escapeXml = value => String(value ?? '').replace(/[<>&'"]/g, character => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;',
}[character]));

const appendStyle = (element, declaration) => {
    const style = (element.getAttribute('style') || '').trim();
    element.setAttribute('style', style ? `${style.replace(/;$/, '')};${declaration}` : declaration);
};

const formatTierName = tier => tier.charAt(0).toUpperCase() + tier.slice(1);

const formatSessionDate = date => (/^\d{4}-\d{2}-\d{2}$/.test(date || '') && dayjs(date).isValid()
    ? dayjs(date).format('ddd, D MMM YYYY') : date || 'Date not set');

export const formatSnapshotTime = date => `${new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    hourCycle: 'h23', timeZone: SEAT_MAP_TIME_ZONE,
}).format(date)} WIB`;

const readViewBox = root => {
    const values = (root.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (values.length === 4 && values.every(Number.isFinite) && values[2] > 0 && values[3] > 0) return values;
    const width = parseFloat(root.getAttribute('width'));
    const height = parseFloat(root.getAttribute('height'));
    if (width > 0 && height > 0) return [0, 0, width, height];
    throw new Error('The venue seat map SVG has no size (viewBox). Upload a corrected SVG in Venue Settings.');
};

const parseSeatMap = svgText => {
    const document = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const root = document.documentElement;
    if (document.getElementsByTagName('parsererror').length || root?.localName !== 'svg') {
        throw new Error('The venue seat map SVG could not be read. Upload a corrected SVG in Venue Settings.');
    }
    return root;
};

/** Tickets left per lower-case tier, from the layout's "Actual ticket availability" rows. */
const getTicketsLeftByTier = capacityRows => {
    if (!Array.isArray(capacityRows) || !capacityRows.length) {
        throw new Error('Ticket availability is not loaded. Refresh the layout before downloading.');
    }
    return new Map(capacityRows.map(row => [String(row.tier).toLowerCase(), Number(row.available) || 0]));
};

// A ticket without a numbered chair still reserves capacity. Operational blocks
// and grey chairs caused by a sold-out tier are not customer assignments.
export const getUnassignedReservations = (seats, capacityRows) => capacityRows.map(row => {
    const tier = String(row.tier).toLowerCase();
    if (!Number.isSafeInteger(row.reserved) || row.reserved < 0) {
        throw new Error('Booked / held ticket counts are not loaded. Refresh the layout before downloading.');
    }
    const assignedLabels = new Set(seats.filter(seat => String(seat.areaType || '').toLowerCase() === tier
        && ['booked', 'reserved', 'locked'].includes(seat.status)).map(seat => seat.seatLabel));
    return { tier, reserved: row.reserved, assigned: assignedLabels.size,
        unassigned: Math.max(0, row.reserved - assignedLabels.size), available: row.available };
});

/**
 * A seat is unavailable when its chair is taken (booked, reserved, locked or blocked)
 * or when its tier has no tickets left, because tickets bought without a seat number
 * still use that tier's capacity.
 */
export const isSeatUnavailable = (seat, ticketsLeftByTier) => {
    const tier = String(seat.areaType || '').toLowerCase();
    if (!ticketsLeftByTier.has(tier)) {
        throw new Error(`Ticket availability for ${seat.areaType || 'an unknown tier'} is missing. Refresh the layout before downloading.`);
    }
    return seat.status !== 'available' || ticketsLeftByTier.get(tier) <= 0;
};

const greyOutSeats = (root, seats, ticketsLeftByTier) => {
    const elementsById = new Map([...root.querySelectorAll('[id]')].map(element => [element.getAttribute('id'), element]));
    const missing = [];
    let unavailableCount = 0;
    seats.forEach(seat => {
        const shape = elementsById.get(seat.seatLabel);
        const number = elementsById.get(`${seat.seatLabel}-num`);
        if (!shape || !number) {
            missing.push(seat.seatLabel);
            return;
        }
        if (!isSeatUnavailable(seat, ticketsLeftByTier)) return;
        appendStyle(shape, `fill:${SEAT_MAP_UNAVAILABLE_FILL}`);
        appendStyle(number, `fill:${SEAT_MAP_UNAVAILABLE_TEXT}`);
        unavailableCount += 1;
    });
    if (missing.length) {
        const sorted = missing.sort((a, b) => a.localeCompare(b, 'en', { numeric: true }));
        const extra = sorted.length > MISSING_SEATS_SHOWN ? ` and ${sorted.length - MISSING_SEATS_SHOWN} more` : '';
        throw new Error(`The venue seat map has no shape for seat ${sorted.slice(0, MISSING_SEATS_SHOWN).join(', ')}${extra}. `
            + 'Upload an SVG that follows the seat map contract in Venue Settings.');
    }
    return unavailableCount;
};

const renderHeader = ({ session, ticketsLeftByTier, generatedAt, width, unit }) => {
    const time = String(session.displayTime || session.time || '').replace('-', '–');
    const ticketsLeft = [...ticketsLeftByTier].map(([tier, left]) => `${formatTierName(tier)} ${left}`).join(' · ');
    const text = (y, size, weight, fill, value) => `<text x="${width / 2}" y="${y * unit}" text-anchor="middle" `
        + `font-family="${FONT_FAMILY}" font-size="${size * unit}" font-weight="${weight}" fill="${fill}">${escapeXml(value)}</text>`;
    return [
        text(38, 24, 700, TEXT_DARK, session.venue),
        text(64, 15, 600, TEXT_DARK, `${formatSessionDate(session.date)} · ${time} WIB`),
        text(90, 12, 500, TEXT_MUTED, `Tickets left: ${ticketsLeft} · Snapshot: ${formatSnapshotTime(generatedAt)}`),
    ].join('');
};

const renderLegend = ({ y, width, unit, hasUnassigned }) => {
    const items = [
        { label: 'Presto', fill: PRESTO_FILL },
        { label: 'Allegro', fill: ALLEGRO_FILL },
        { label: 'Not available', fill: SEAT_MAP_UNAVAILABLE_FILL },
        ...(hasUnassigned ? [{ label: 'Awaiting assignment', fill: UNASSIGNED_FILL }] : []),
    ];
    const itemWidth = (hasUnassigned ? 180 : 140) * unit;
    const startX = (width - itemWidth * items.length) / 2;
    return items.map((item, index) => {
        const x = startX + index * itemWidth;
        return `<rect x="${x}" y="${y + 14 * unit}" width="${18 * unit}" height="${18 * unit}" rx="${4 * unit}" fill="${item.fill}"/>`
            + `<text x="${x + 26 * unit}" y="${y + 28 * unit}" font-family="${FONT_FAMILY}" font-size="${13 * unit}" `
            + `font-weight="600" fill="${TEXT_DARK}">${escapeXml(item.label)}</text>`;
    }).join('');
};

const renderReservationPanels = ({ reservations, root, seats, x, headerHeight, mapHeight, minY, unit }) => {
    const numbersById = new Map([...root.querySelectorAll('[id]')].map(element => [element.getAttribute('id'), element]));
    let bottom = headerHeight;
    const panels = reservations.map(reservation => {
        const tierSeats = seats.filter(seat => String(seat.areaType || '').toLowerCase() === reservation.tier);
        const positions = tierSeats.map(seat => numbersById.get(`${seat.seatLabel}-num`)?.getAttribute('y'))
            .filter(value => value !== null && value !== undefined).map(parseFloat).filter(Number.isFinite);
        const anchorY = positions.length ? Math.min(...positions) - minY : mapHeight / 2;
        const y = Math.max(headerHeight, headerHeight + anchorY - 24 * unit, bottom);
        const symbolRows = Math.ceil(reservation.unassigned / RESERVATION_SYMBOL_COLUMNS);
        const height = (158 + symbolRows * 24) * unit;
        const text = (offsetY, size, weight, value) => `<text x="${x + 16 * unit}" y="${y + offsetY * unit}" `
            + `font-family="${FONT_FAMILY}" font-size="${size * unit}" font-weight="${weight}" fill="${UNASSIGNED_INK}">${escapeXml(value)}</text>`;
        const symbols = Array.from({ length: reservation.unassigned }, (_, index) => {
            const symbolX = x + (16 + (index % RESERVATION_SYMBOL_COLUMNS) * 24) * unit;
            const symbolY = y + (88 + Math.floor(index / RESERVATION_SYMBOL_COLUMNS) * 24) * unit;
            return `<g data-reservation-symbol="${escapeXml(reservation.tier)}">`
                + `<rect x="${symbolX}" y="${symbolY}" width="${18 * unit}" height="${14 * unit}" rx="${3 * unit}" fill="${UNASSIGNED_FILL}" stroke="${UNASSIGNED_INK}" stroke-width="${unit}"/>`
                + `<path d="M${symbolX},${symbolY + 18 * unit}h${18 * unit}" stroke="${UNASSIGNED_INK}" stroke-width="${2 * unit}"/>`
                + '</g>';
        }).join('');
        bottom = y + height + RESERVATION_PANEL_GAP * unit;
        return `<g data-unassigned-tier="${escapeXml(reservation.tier)}">`
            + `<rect x="${x}" y="${y}" width="${RESERVATION_PANEL_WIDTH * unit}" height="${height}" rx="${10 * unit}" `
            + `fill="${UNASSIGNED_BACKGROUND}" stroke="#C78B1D" stroke-width="${1.5 * unit}"/>`
            + text(26, 16, 700, formatTierName(reservation.tier))
            + text(51, 20, 700, `${reservation.unassigned} ${reservation.unassigned === 1 ? 'ticket' : 'tickets'} reserved`)
            + text(72, 13, 600, 'Seats awaiting assignment')
            + symbols
            + text(108 + symbolRows * 24, 11, 500, `${reservation.reserved} total booked/held · ${reservation.assigned} chairs assigned`)
            + text(129 + symbolRows * 24, 14, 700, `${reservation.available} tickets available for sale`)
            + text(146 + symbolRows * 24, 10, 500, 'Symbols do not identify numbered chairs.')
            + '</g>';
    }).join('');
    return { svg: panels, bottom: reservations.length ? bottom : headerHeight };
};

/**
 * Builds a downloadable seat map for one competition session.
 *
 * @param {object} params
 * @param {string} params.svgText Uploaded venue SVG that follows the seat map contract.
 * @param {Array<{ seatLabel: string, status: string, areaType: string }>} params.seats Current seats of the session.
 * @param {Array<{ tier: string, available: number, reserved: number }>} params.capacityRows Actual ticket availability rows.
 * @param {{ venue: string, date: string, time: string, displayTime?: string }} params.session
 * @param {Date} params.generatedAt When the seat and availability data were loaded.
 * @returns {{ svg: string, width: number, height: number, unavailableCount: number }}
 * @throws {Error} When the SVG is unreadable, misses a seat, or availability is incomplete.
 */
export const buildSeatMapSnapshot = ({ svgText, seats, capacityRows, session, generatedAt }) => {
    if (!Array.isArray(seats) || !seats.length) throw new Error('This session has no numbered seats to draw.');
    const ticketsLeftByTier = getTicketsLeftByTier(capacityRows);
    const root = parseSeatMap(svgText);
    const [, minY, mapWidth, mapHeight] = readViewBox(root);
    const unavailableCount = greyOutSeats(root, seats, ticketsLeftByTier);
    const reservations = getUnassignedReservations(seats, capacityRows).filter(row => row.unassigned > 0);

    const unit = mapWidth / BASE_MAP_WIDTH;
    const padding = SIDE_PADDING * unit;
    const hasUnassigned = reservations.length > 0;
    const width = mapWidth + 2 * padding + (hasUnassigned ? (RESERVATION_PANEL_WIDTH + RESERVATION_PANEL_GAP) * unit : 0);
    const headerHeight = HEADER_HEIGHT * unit;
    const reservationPanels = renderReservationPanels({
        reservations, root, seats, x: padding + mapWidth + RESERVATION_PANEL_GAP * unit,
        headerHeight, mapHeight, minY, unit,
    });
    const legendY = Math.max(headerHeight + mapHeight, reservationPanels.bottom);
    const height = legendY + LEGEND_HEIGHT * unit;
    root.setAttribute('x', String(padding));
    root.setAttribute('y', String(headerHeight));
    root.setAttribute('width', String(mapWidth));
    root.setAttribute('height', String(mapHeight));

    const svg = `<svg xmlns="${SVG_NAMESPACE}" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`
        + `<rect width="100%" height="100%" fill="#FFFFFF"/>`
        + renderHeader({ session, ticketsLeftByTier, generatedAt, width, unit })
        + new XMLSerializer().serializeToString(root)
        + reservationPanels.svg
        + renderLegend({ y: legendY, width, unit, hasUnassigned })
        + '</svg>';
    return { svg, width, height, unavailableCount };
};

export const getSeatMapFileName = session => {
    const part = value => String(value || '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return `${[part(session.venue), part(session.date), part(session.time)].filter(Boolean).join('_')}_seats.png`;
};

/** Browser only: rasterises a snapshot to a PNG blob at SEAT_MAP_PNG_SCALE. */
export const renderSeatMapPng = (snapshot, scale = SEAT_MAP_PNG_SCALE) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([snapshot.svg], { type: 'image/svg+xml;charset=utf-8' }));
    const image = new Image();
    image.onload = () => {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(snapshot.width * scale);
            canvas.height = Math.round(snapshot.height * scale);
            const context = canvas.getContext('2d');
            context.fillStyle = '#FFFFFF';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('The seat map image could not be created.'))), 'image/png');
        } catch (error) {
            reject(error);
        } finally {
            URL.revokeObjectURL(url);
        }
    };
    image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('The seat map image could not be rendered.'));
    };
    image.src = url;
});
